// src/components/ConfirmChangesModal/ConfirmChangesModal.tsx
import React, { useMemo, useState } from 'react';
import './ConfirmChangesModal.css';

export type Decision = 'now' | 'next';
export type DecisionsMap = Record<string, Decision>;

type Medication = {
  id: string;
  name: string;
  instruction?: string;
  isNew?: boolean;
  isEdit?: boolean;
  isRemoval?: boolean;
};

type Props = {
  meds: Medication[];
  onCancel: () => void;
  onConfirm: (decisions: DecisionsMap, confirmPrescriptions: boolean) => void;
};

const ConfirmChangesModal: React.FC<Props> = ({ meds, onCancel, onConfirm }) => {
  // per-med choice
  const [choices, setChoices] = useState<DecisionsMap>({});
  // final confirmation checkbox
  const [agree, setAgree] = useState(false);

  const addedOrEdited = useMemo(() => meds.filter((m) => m.isNew || m.isEdit), [meds]);
  const removed = useMemo(() => meds.filter((m) => m.isRemoval), [meds]);

  //const setChoice = (id: string, v: Decision) => setChoices((prev) => ({ ...prev, [id]: v }));
  type GroupKey = 'add_edit' | 'remove';

  const groupKeyFor = (m: Medication): GroupKey => (m.isRemoval ? 'remove' : 'add_edit');

  const setChoiceForGroup = (group: GroupKey, v: Decision) => {
    setChoices((prev) => {
      const next = { ...prev };
      meds.forEach((m) => {
        if (groupKeyFor(m) === group) next[m.id] = v;
      });
      return next;
    });
  };

  const allHaveChoices =
    meds.length > 0 && meds.every((m) => choices[m.id] === 'now' || choices[m.id] === 'next');

  const canSend = allHaveChoices && agree;

  return (
    <div className="ccm-overlay" role="dialog" aria-modal="true">
      <div className="ccm-modal">
        <div className="ccm-header">
          <div className="ccm-title">
            Þú hefur lagt til eftirfarandi breytingar.
            <br />
            Hvenær eiga þær að taka gildi?
          </div>
        </div>

        {!!addedOrEdited.length && (
          <>
            <div className="ccm-group-title">Ný lyf eða skammtaaukning</div>
            <div className="ccm-group">
              {addedOrEdited.map((m) => (
                <div key={m.id} className="ccm-med-card add">
                  <div className="ccm-med-top">
                    <div className="ccm-med-name">
                      {m.name}
                      {m.instruction ? ` – ${m.instruction}` : ''}
                    </div>
                  </div>

                  <div className="ccm-row">
                    <div className="ccm-row-label">Breyting tekur gildi</div>
                    <div className="ccm-row-choices">
                      <button
                        className={`ccm-pill ${choices[m.id] === 'now' ? 'selected' : ''}`}
                        onClick={() => setChoiceForGroup('add_edit', 'now')}
                        type="button"
                      >
                        Sem fyrst
                      </button>
                      <button
                        className={`ccm-pill ${choices[m.id] === 'next' ? 'selected' : ''}`}
                        onClick={() => setChoiceForGroup('add_edit', 'next')}
                        type="button"
                      >
                        Frá næstu skömmtun
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!!removed.length && (
          <>
            <div className="ccm-group-title">Lyfjameðferð hætt eða skammta­minnkun</div>
            <div className="ccm-group">
              {removed.map((m) => (
                <div key={m.id} className="ccm-med-card remove">
                  <div className="ccm-med-top">
                    <div className="ccm-med-name">
                      {m.name}
                      {/* no instruction for removals */}
                    </div>
                  </div>

                  <div className="ccm-row">
                    <div className="ccm-row-label">Breyting tekur gildi</div>
                    <div className="ccm-row-choices">
                      <button
                        className={`ccm-pill ${choices[m.id] === 'now' ? 'selected' : ''}`}
                        onClick={() => setChoiceForGroup('remove', 'now')}
                        type="button"
                      >
                        Sem fyrst
                      </button>
                      <button
                        className={`ccm-pill ${choices[m.id] === 'next' ? 'selected' : ''}`}
                        onClick={() => setChoiceForGroup('remove', 'next')}
                        type="button"
                      >
                        Frá næstu skömmtun
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <label className="ccm-agree">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          Ég staðfesti hér með að ég er búin að senda viðeigandi lyfseðla!
        </label>

        <div className="ccm-footer">
          <button className="ccm-cancel" onClick={onCancel} type="button">
            Hætta við
          </button>

          {canSend && (
            <button className="ccm-confirm" onClick={() => onConfirm(choices, agree)} type="button">
              Senda í skömmtun
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmChangesModal;
